import { useApolloClient, useQuery } from "@apollo/client";
import { Dictionary } from "lodash";
import differenceBy from "lodash/differenceBy";
import groupBy from "lodash/groupBy";
import keyBy from "lodash/keyBy";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";

import {
  UserAttributeCategoryFragment,
  UserAttributeCategoryQuery,
  UserAttributeCategoryQueryVariables,
  UserAttributeFragment,
  UserAttributeListQuery,
  UserAttributeQuery,
  UserAttributeQueryVariables,
} from "gql/graphql";
import useBurstQuery from "modules/hooks/use-burst-query";
import { useAuth } from "../auth-provider";
import USER_ATTRIBUTE_LIST_QUERY from "./networking/user-attribute-list";
import USER_ATTRIBUTE_CATEGORY_QUERY from "./networking/user-attribute-category";
import USER_ATTRIBUTE_QUERY from "./networking/user-attribute";
import { sortUserAttributeCategories } from "modules/utils/Functions";

export interface IProviderProps {
  children?: ReactNode;
}

interface IContextProps {
  userAttributeCategories: readonly UserAttributeCategoryFragment[];
  userAttributeCategoriesById: Dictionary<
    UserAttributeCategoryFragment | undefined
  >;

  userAttributes: readonly UserAttributeFragment[];
  userAttributesById: Dictionary<UserAttributeFragment | undefined>;
  userAttributesByUserAttributeCategoryId: Dictionary<UserAttributeFragment[]>;

  refreshUserAttributeCategories: (id: string[]) => void;
  refreshUserAttributes: (id: string[]) => void;
}

const UserAttributesContext = createContext<IContextProps>({
  userAttributeCategories: [],
  userAttributeCategoriesById: {},

  userAttributes: [],
  userAttributesById: {},
  userAttributesByUserAttributeCategoryId: {},

  refreshUserAttributeCategories: () => {},
  refreshUserAttributes: () => {},
});

export const UserAttributesProvider = ({ children }: IProviderProps) => {
  const { cache } = useApolloClient();
  const { organization } = useAuth();
  const { isLoggedIn } = useAuth();

  const { data } = useQuery<UserAttributeListQuery>(USER_ATTRIBUTE_LIST_QUERY, {
    skip: !isLoggedIn,
  });

  const [userAttributeCategoryAPI] = useBurstQuery<
    UserAttributeCategoryQuery,
    UserAttributeCategoryQueryVariables
  >(USER_ATTRIBUTE_CATEGORY_QUERY);

  const [userAttributeAPI] = useBurstQuery<
    UserAttributeQuery,
    UserAttributeQueryVariables
  >(USER_ATTRIBUTE_QUERY);

  const userAttributeCategories = useMemo(
    () =>
      (data ? [...data.userAttributeCategoryList] : []).sort(
        sortUserAttributeCategories(organization),
      ),
    [data, organization],
  );

  const userAttributeCategoriesById = useMemo(
    () => keyBy(userAttributeCategories || [], "id"),
    [userAttributeCategories],
  );

  const userAttributes = useMemo(() => data?.userAttributeList || [], [data]);

  const userAttributesById = useMemo(
    () => keyBy(userAttributes || [], "id"),
    [userAttributes],
  );

  const userAttributesByUserAttributeCategoryId = useMemo(
    () => groupBy(userAttributes, "userAttributeCategoryId"),
    [userAttributes],
  );

  const refreshUserAttributeCategories = useCallback(
    (idList: string[]) => {
      userAttributeCategoryAPI({ variables: { id: idList } })
        .then((result) => {
          const userAttributeCategories = result.data?.userAttributeCategory;
          if (!userAttributeCategories) {
            return;
          }

          cache.updateQuery<UserAttributeListQuery>(
            {
              query: USER_ATTRIBUTE_LIST_QUERY,
            },
            (data) => {
              if (!data) {
                return data;
              }
              const _userAttributeCategoryList = data.userAttributeCategoryList;

              const filteredCategories = _userAttributeCategoryList.filter(
                (userAttributeCategory) =>
                  !idList.includes(userAttributeCategory.id),
              );

              const newUserAttributeCategories = differenceBy(
                userAttributeCategories,
                filteredCategories,
                "id",
              );

              return {
                ...data,
                userAttributeCategoryList: [
                  ...filteredCategories,
                  ...newUserAttributeCategories,
                ],
              };
            },
          );
        })
        .catch(() => {
          // best-effort refresh: on a failed fetch the cached list stays as-is
        });
    },
    [cache, userAttributeCategoryAPI],
  );

  const refreshUserAttributes = useCallback(
    (idList: string[]) => {
      userAttributeAPI({ variables: { id: idList } })
        .then((result) => {
          const userAttributes = result.data?.userAttributes;
          if (!userAttributes) {
            return;
          }

          cache.updateQuery<UserAttributeListQuery>(
            {
              query: USER_ATTRIBUTE_LIST_QUERY,
            },
            (data) => {
              if (!data) {
                return data;
              }
              const _userAttributeList = data.userAttributeList;

              const filteredUserAttributes = _userAttributeList.filter(
                (userAttribute) => !idList.includes(userAttribute.id),
              );

              const newUserAttributes = differenceBy(
                userAttributes,
                filteredUserAttributes,
                "id",
              );

              return {
                ...data,
                userAttributeList: [
                  ...filteredUserAttributes,
                  ...newUserAttributes,
                ],
              };
            },
          );
        })
        .catch(() => {
          // best-effort refresh: on a failed fetch the cached list stays as-is
        });
    },
    [cache, userAttributeAPI],
  );

  const value = useMemo<IContextProps>(
    () => ({
      userAttributeCategories,
      userAttributeCategoriesById,

      userAttributes,
      userAttributesById,
      userAttributesByUserAttributeCategoryId,

      refreshUserAttributeCategories,
      refreshUserAttributes,
    }),
    [
      refreshUserAttributeCategories,
      refreshUserAttributes,
      userAttributeCategories,
      userAttributeCategoriesById,
      userAttributes,
      userAttributesById,
      userAttributesByUserAttributeCategoryId,
    ],
  );

  return (
    <UserAttributesContext.Provider value={value}>
      {children}
    </UserAttributesContext.Provider>
  );
};

export const useUserAttributes = () => useContext(UserAttributesContext);
